import { useCallback, useRef } from 'react';

import { fg } from '@confluence/feature-gating';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { TWCG_1489_CONNECTOR_ONBOARDING } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import { useConnectorOnboardingState } from './useConnectorOnboardingState.ts';

import type { ConnectorPromoConfig, PromoDismissed } from '../connector-promo-config-types.ts';
import { getDaysSinceEnrolment } from '../utils/enrolmentWindow.ts';
import { getRecommendedConnectors } from '../utils/getRecommendedConnectors.ts';

import { useConnectorOnboardingTeamType } from './useConnectorOnboardingTeamType.ts';
import { useConnectorStates } from './useConnectorStates.ts';
import { useSetupConnector } from './useSetupConnector.ts';

/**
 * Computes the next PromoDismissed value when a surface is dismissed.
 * Merges with the existing state so sidebar and banner can be dismissed independently.
 */
function getNextPromoDismissed(
	current: PromoDismissed | undefined,
	surface: 'sidebar' | 'banner',
): PromoDismissed {
	// Not set or current is invalid
	if (!current || !['sidebar', 'banner'].includes(current)) {
		return surface;
	}
	if (current === surface) {
		return surface;
	}
	return 'both';
}

const DISABLED_CONFIG: ConnectorPromoConfig = {
	recommendedConnectors: [],
	spotlightShown: false,
	enrolmentDate: null,
	shouldShowEmptyState: false,
	shouldShowNthPromo: false,
	shouldShowNthPagePromo: false,
	shouldShowPostConnectPromo: false,
	shouldShowPostConnectPagePromo: false,
	shouldShowSidebarItem: false,
	shouldShowSpotlight: false,
	onSetupConnector: () => {},
	onDismissPromo: () => {},
	onMaybeLater: () => {},
	onDismissSpotlight: () => {},
	activeConnectorKey: null,
	closeModal: () => {},
};

export function useConnectorPromoConfig({
	hasRecentItems = false,
}: {
	hasRecentItems?: boolean;
} = {}): ConnectorPromoConfig {
	// No experiment check here. Surface components gate rendering via
	// useIsConnectorOnboardingEnabled() before calling this hook.
	const { teamType } = useConnectorOnboardingTeamType({
		skip: !fg('twcg-1489-cc-3p-connector-onboarding-fg'),
	});
	const {
		connectorStates,
		connectedCount,
		loading: connectorStatesLoading,
		error: connectorStatesError,
	} = useConnectorStates({ skip: !fg('twcg-1489-cc-3p-connector-onboarding-fg') });

	const { openSetupModal, activeConnectorKey, closeModal } = useSetupConnector();
	const { setOnboardingState } = useSetOnboardingState();

	// Carries the latest promoDismissed value into callbacks without violating rules-of-hooks.
	// Updated on every render after the loading/error early returns.
	const promoDismissedRef = useRef<PromoDismissed | undefined>(undefined);

	const {
		onboardingState: connectorOnboardingState,
		loading: onboardingStateLoading,
		error: onboardingStateError,
		updateOnboardingState,
	} = useConnectorOnboardingState({
		skip: !fg('twcg-1489-cc-3p-connector-onboarding-fg'),
	});

	// Stable callback refs — must be declared before any early returns to satisfy rules-of-hooks
	const onSetupConnector = useCallback(
		(connectorKey: string) => openSetupModal(connectorKey),
		[openSetupModal],
	);
	const onDismissPromo = useCallback(
		(surface: 'sidebar' | 'banner') => {
			const next = getNextPromoDismissed(promoDismissedRef.current, surface);
			if (next === promoDismissedRef.current) {
				return;
			}

			const update = {
				key: TWCG_1489_CONNECTOR_ONBOARDING.PROMO_DISMISSED,
				value: next,
			};
			setOnboardingState(update);
			updateOnboardingState(update);
			promoDismissedRef.current = next;
		},
		[setOnboardingState, updateOnboardingState],
	);
	const onMaybeLater = useCallback(() => {}, []);
	const onDismissSpotlight = useCallback(() => {
		const update = {
			key: TWCG_1489_CONNECTOR_ONBOARDING.SPOTLIGHT_SHOWN,
			value: 'true',
		};
		setOnboardingState(update);
		updateOnboardingState(update);
	}, [setOnboardingState, updateOnboardingState]);

	if (onboardingStateError) {
		if (isUnauthorizedError(onboardingStateError)) {
			markErrorAsHandled(onboardingStateError);
		}
		return DISABLED_CONFIG;
	}

	if (connectorStatesError) {
		if (isUnauthorizedError(connectorStatesError)) {
			markErrorAsHandled(connectorStatesError);
		}
		return DISABLED_CONFIG;
	}

	if (onboardingStateLoading || connectorStatesLoading) {
		return DISABLED_CONFIG;
	}

	// If 2+ connectors are already configured org-wide the site is not a
	// good onboarding target; hide the entire experiment.
	if (connectedCount >= 2) {
		return DISABLED_CONFIG;
	}

	const { recommendedConnectors, connectedAppName } = getRecommendedConnectors(
		teamType,
		connectorStates,
	);
	const hasConnectedAny = connectedAppName !== undefined;
	const nextRecommendedConnector = recommendedConnectors[0];

	const promoDismissed = connectorOnboardingState?.promoDismissed;

	// Keep the ref in sync so dismiss callbacks always close over the latest value.
	promoDismissedRef.current = promoDismissed;
	const spotlightShown = !!connectorOnboardingState?.spotlightShown;

	const enrolmentDateStr = connectorOnboardingState?.enrolmentDate;
	const enrolmentDate = enrolmentDateStr ? new Date(enrolmentDateStr) : null;
	const daysSinceEnrolment = getDaysSinceEnrolment(enrolmentDateStr);

	const sidebarDismissed = promoDismissed === 'sidebar' || promoDismissed === 'both';
	const bannerDismissed = promoDismissed === 'banner' || promoDismissed === 'both';

	return {
		recommendedConnectors,
		connectedAppName,
		nextRecommendedConnector,
		spotlightShown,
		enrolmentDate,
		promoDismissed,
		shouldShowEmptyState: !hasConnectedAny && !hasRecentItems,
		shouldShowNthPromo: hasRecentItems && !hasConnectedAny && !bannerDismissed,
		shouldShowNthPagePromo: hasRecentItems && !hasConnectedAny && !bannerDismissed,
		shouldShowPostConnectPromo: hasConnectedAny && !bannerDismissed,
		shouldShowPostConnectPagePromo: hasConnectedAny && !bannerDismissed,
		shouldShowSidebarItem: !sidebarDismissed,
		shouldShowSpotlight: !!enrolmentDate && daysSinceEnrolment >= 3 && !spotlightShown,
		onSetupConnector,
		onDismissPromo,
		onMaybeLater,
		onDismissSpotlight,
		activeConnectorKey,
		closeModal,
	};
}
